import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import ErrorBoundary from './components/ErrorBoundary';
import { installThemeIconErrorSuppressor } from './utils/suppressThemeIconErrors';
import { initReplay } from './utils/sessionReplay';

// VI-784: register FIRST — before the CloudWatch RUM client below and before the theme's icon
// converter can throw — so the known upstream replaceChild errors (PA-1976) are kept out of both
// the console and RUM. Suppression only; counted on window.__jtSuppressedThemeIconErrors.
installThemeIconErrorSuppressor();

axiosRetry(axios, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) =>
    axiosRetry.isNetworkOrIdempotentRequestError(error) ||
    error.response?.status === 502 ||
    error.response?.status === 503 ||
    error.response?.status === 504,
});

// ---------------------------------------------------------------------------
// Session replay records what the candidate saw; CloudWatch RUM below keeps
// performance, errors, HTTP and traces. Started before it because the recorder
// wraps fetch, XHR and console as it starts, and anything above is a blind spot.
// ---------------------------------------------------------------------------
initReplay();

// ---------------------------------------------------------------------------
// CloudWatch RUM (AWS)
// ---------------------------------------------------------------------------
const rumAppMonitorId = import.meta.env.VITE_RUM_APP_MONITOR_ID;
const rumIdentityPoolId = import.meta.env.VITE_RUM_IDENTITY_POOL_ID;

if (rumAppMonitorId && rumIdentityPoolId) {
  try {
    const config: AwsRumConfig = {
      sessionSampleRate: 1,
      identityPoolId: rumIdentityPoolId,
      endpoint: 'https://dataplane.rum.us-east-1.amazonaws.com',
      // No RRWebPlugin: session replay is @jobtarget/browser-rum's job now, and
      // two DOM recorders in one document both patch fetch, XHR and console.
      telemetries: [
        'errors',
        'performance',
        [
          'http',
          {
            // Without this a request is recorded but linked to nothing on the
            // backend, so the RUM console's Traces column stays empty.
            addXRayTraceIdHeader: true,
            recordAllRequests: true,
            // aws-rum-web matches the URL as the caller wrote it, so an
            // absolute-only pattern traces nothing for relative fetches.
            urlsToInclude: [/^\/(?!\/)/, /^https?:\/\/[^/?#]*\.jobtarget\.com([/:?#]|$)/],
            // The replay intake is a *.jobtarget.com host, so the include pattern
            // catches it: RUM would instrument the recorder's own uploads, one
            // billable event and one X-Ray segment per chunk.
            urlsToExclude: [/rum-replay-intake\.jobtarget\.com/],
          },
        ],
      ],
      allowCookies: true,
      enableXRay: true,
      sessionEventLimit: 0,
    };

    const awsRum = new AwsRum(
      rumAppMonitorId,
      // The release, not a literal. VITE_DD_VERSION is kept as a fallback because
      // it is what this build has historically set.
      import.meta.env.VITE_APP_RELEASE || import.meta.env.VITE_DD_VERSION || '',
      'us-east-1',
      config
    );
    // Join the two: the replay SDK pins its session id onto the RUM session, so
    // the console's CloudWatch RUM deep links resolve. pinSessionId is a real 3.x
    // command that aws-rum-web omits from its declarations.
    void import('@jobtarget/browser-rum').then(({ registerCloudWatchRum }) =>
      registerCloudWatchRum(awsRum as unknown as { pinSessionId: (id: string) => void })
    );
  } catch (error) {
    console.error('CloudWatch RUM init error:', error);
  }
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

reportWebVitals();
